import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../../../utils/api';

/**
 * Underleverantörer kopplade till ett byggföretag.
 *
 * Kopplingen avgör vilka prisunderlag en UE kan få se: byggaren kan dela ett PU
 * med sin elektriker eller rörmokare, som då ser sina egna timmar och sin
 * arbetsbeskrivning och kan tycka till innan byggaren svarar byggkonsulten.
 *
 * Samma UE kan vara kopplad till flera byggföretag — en elektriker jobbar ofta
 * för flera byggare — så listan ligger på byggföretaget och en bock här tar inte
 * bort hen någon annanstans.
 *
 * Vilka YRKESGRUPPER kopplingen ger kryssas i per UE. `contractorType` på kontot
 * ger bara ett förval: typen har nio värden medan kalkylen har 34 yrkesgrupper,
 * så en plattsättare som också lägger tätskikt och flytspacklar måste kunna
 * kryssa i alla tre. Listan och förvalen kommer från servern (shared/
 * ueYrkesgrupper.js) — yrkesgruppsnamnen finns redan i sju parallella kopior i
 * kodbasen och ska inte få en åttonde här.
 *
 * Sparar via sin EGEN endpoint, inte via formulärets Spara-knapp. Därför är
 * varje knapp `type="button"`: en submit-knapp hade skickat hela
 * byggföretagsformuläret.
 */
const BuilderSubcontractorsTab = ({ builderId }) => {
  const [tillgangliga, setTillgangliga] = useState([]);
  const [valda, setValda] = useState(new Set());
  const [kopplade, setKopplade] = useState([]);
  const [valbara, setValbara] = useState([]);
  // userId -> Set(normaliserade yrkesgruppsnycklar)
  const [ygVal, setYgVal] = useState({});
  const [oppen, setOppen] = useState(null); // userId vars kryssrutor är utfällda
  const [laddar, setLaddar] = useState(true);
  const [sparar, setSparar] = useState(false);
  const [fel, setFel] = useState(null);
  const [kvitto, setKvitto] = useState(null);
  const [skickar, setSkickar] = useState(null); // userId

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  // Materialgrupperna för en uppsättning nycklar, slagna ihop utan dubbletter.
  // Räknas här ur `valbara` så fliken visar exakt det servern skulle ge UE:n.
  const materialFor = useCallback((nycklar) => {
    const ut = [];
    for (const kat of valbara) {
      for (const g of kat.grupper) {
        if (!nycklar.includes(g.nyckel)) continue;
        for (const m of (g.materialgrupper || [])) if (!ut.includes(m)) ut.push(m);
      }
    }
    return ut;
  }, [valbara]);

  const namnFor = useCallback((nycklar) => {
    const karta = new Map();
    for (const kat of valbara) for (const g of kat.grupper) karta.set(g.nyckel, g.namn);
    return nycklar.map(n => karta.get(n) || n);
  }, [valbara]);

  const hamta = useCallback(async () => {
    if (!builderId) return;
    setLaddar(true);
    setFel(null);
    try {
      const res = await fetch(`${API_URL}/builders/${builderId}/subcontractors`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Kunde inte hämta underleverantörer');
      setTillgangliga(data.tillgangliga || []);
      setKopplade(data.kopplade || []);
      setValbara(data.valbaraYrkesgrupper || []);
      setValda(new Set((data.kopplade || []).map(k => String(k.user._id))));
      // Seedas från vad kopplingen FAKTISKT ger, inte från det sparade fältet:
      // en koppling med tom lista visar sitt förval ikryssat, vilket är det som
      // gäller i dag — annars hade rutorna sett tomma ut fast UE:n ser rader.
      setYgVal(Object.fromEntries(
        (data.kopplade || []).map(k => [String(k.user._id), k.yrkesgrupper || []])
      ));
    } catch (e) {
      setFel(e.message);
    } finally {
      setLaddar(false);
    }
  }, [builderId]);

  useEffect(() => { hamta(); }, [hamta]);

  const toggla = (userId) => {
    setKvitto(null);
    const kryssasI = !valda.has(userId);
    setValda(prev => {
      const nasta = new Set(prev);
      if (nasta.has(userId)) nasta.delete(userId);
      else nasta.add(userId);
      return nasta;
    });
    if (kryssasI) {
      // Seeda förvalet direkt, annars sparas en koppling utan yrkesgrupper.
      setYgVal(prev => {
        if (prev[userId]?.length) return prev;
        const u = tillgangliga.find(x => String(x._id) === userId);
        return { ...prev, [userId]: [...(u?.forvalYrkesgrupper || [])] };
      });
      setOppen(userId);
    } else if (oppen === userId) {
      setOppen(null);
    }
  };

  const togglaYg = (userId, nyckel) => {
    setKvitto(null);
    setYgVal(prev => {
      const nuvarande = prev[userId] || [];
      return {
        ...prev,
        [userId]: nuvarande.includes(nyckel)
          ? nuvarande.filter(n => n !== nyckel)
          : [...nuvarande, nyckel]
      };
    });
  };

  const spara = async () => {
    setSparar(true);
    setFel(null);
    setKvitto(null);
    try {
      const karta = {};
      for (const id of valda) karta[id] = ygVal[id] || [];
      const res = await fetch(`${API_URL}/builders/${builderId}/subcontractors`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ userIds: [...valda], yrkesgrupper: karta })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Kunde inte spara');
      setKopplade(data.kopplade || []);
      setTillgangliga(data.tillgangliga || []);
      setValbara(data.valbaraYrkesgrupper || valbara);
      setYgVal(Object.fromEntries(
        (data.kopplade || []).map(k => [String(k.user._id), k.yrkesgrupper || []])
      ));
      setOppen(null);
      setKvitto('Underleverantörerna sparade');
    } catch (e) {
      setFel(e.message);
    } finally {
      setSparar(false);
    }
  };

  const skickaUppgifter = async (userId, epost) => {
    setSkickar(userId);
    setFel(null);
    setKvitto(null);
    try {
      const res = await fetch(
        `${API_URL}/builders/${builderId}/subcontractors/${userId}/credentials`,
        { method: 'POST', headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Kunde inte skicka');
      setKvitto(data.message || `Inloggningsuppgifter skickade till ${epost}`);
    } catch (e) {
      setFel(e.message);
    } finally {
      setSkickar(null);
    }
  };

  // Bara sparade kopplingar kan få uppgifter skickade — servern kräver det, och
  // en nyss ikryssad ruta är inte sparad än.
  const sparadKoppling = (userId) => kopplade.some(k => String(k.user._id) === String(userId));

  // Ordningen är betydelselös — kryssrutorna läggs till i klickordning, servern
  // sparar dem som de kommer. Jämför som mängder.
  const sammaLista = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    const bSet = new Set(b);
    return a.every(v => bSet.has(v));
  };

  const osparadeAndringar = valda.size !== kopplade.length
    || kopplade.some(k => !valda.has(String(k.user._id)))
    || kopplade.some(k => !sammaLista(ygVal[String(k.user._id)], k.yrkesgrupper));

  // En koppling utan yrkesgrupper ger UE:n ingenting att se, och servern avvisar
  // den. Spärra Spara här i stället för att låta anropet gå och misslyckas.
  const utanYrkesgrupp = [...valda].filter(id => !(ygVal[id] || []).length);

  const namn = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;

  if (laddar) {
    return <p className="text-sm text-gray-500 py-6 text-center">Laddar underleverantörer…</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Underleverantörer</h3>
        <p className="text-sm text-gray-600">
          Kryssa i vilka underleverantörer som hör till det här byggföretaget, och vilka
          yrkesgrupper var och en ska se. Byggaren kan sedan dela ett prisunderlag med
          dem, och de ser bara sina egna yrkesgruppers timmar, materialkostnaden för de
          materialgrupper som hör dit, och arbetsbeskrivningen — aldrig byggarens
          timpriser eller kundpriset.
        </p>
      </div>

      {fel && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{fel}</div>
      )}
      {kvitto && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">{kvitto}</div>
      )}

      {tillgangliga.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
          Det finns inga användare med rollen <strong>Underleverantör</strong> ännu. Lägg
          upp dem under Användare först — med företagsnamn och typ (El, VVS,
          Plattsättning, Måleri) — så kan de kopplas här.
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {tillgangliga.map((u) => {
            const id = String(u._id);
            const ikryssad = valda.has(id);
            const mina = ygVal[id] || [];
            const utfalld = oppen === id;
            return (
              <div key={id} className="p-3">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={ikryssad}
                    onChange={() => toggla(id)}
                    id={`ue-${id}`}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink"
                  />
                  <label htmlFor={`ue-${id}`} className="flex-1 min-w-0 cursor-pointer">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-medium text-gray-900">{namn(u)}</span>
                      {u.contractorType && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                          {u.contractorType}
                        </span>
                      )}
                      {u.status !== 'active' && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          Inaktiv
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 break-all">
                      {u.companyName || 'Företag ej angivet'} · {u.email}
                    </div>
                  </label>

                  {sparadKoppling(id) && (
                    <button
                      type="button"
                      onClick={() => skickaUppgifter(id, u.email)}
                      disabled={skickar === id || !u.email}
                      title={u.email
                        ? (u.lastLogin
                          ? 'Skickar ett nytt lösenord'
                          : 'Har aldrig loggat in — skicka inloggningsuppgifter')
                        : 'Saknar e-postadress'}
                      className="flex-shrink-0 text-xs px-2 py-1 rounded border border-leadme-pink text-leadme-pink hover:bg-pink-50 disabled:opacity-50"
                    >
                      {skickar === id
                        ? 'Skickar…'
                        : (u.lastLogin ? 'Nytt lösenord' : 'Skicka uppgifter')}
                    </button>
                  )}
                </div>

                {ikryssad && (
                  <div className="mt-2 ml-7">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs">
                      {mina.length > 0 ? (
                        <>
                          <span className="text-gray-500">Ser timmar för</span>
                          <span className="text-gray-800 font-medium">
                            {namnFor(mina).join(', ')}
                          </span>
                          {materialFor(mina).length > 0 && (
                            <>
                              <span className="text-gray-400">·</span>
                              <span className="text-gray-500">material</span>
                              <span className="text-gray-800 font-medium">
                                {materialFor(mina).join(', ')}
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="text-red-600 font-medium">
                          Ingen yrkesgrupp ikryssad — UE:n skulle inte se något
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setOppen(utfalld ? null : id)}
                        className="text-leadme-pink hover:underline"
                      >
                        {utfalld ? 'Stäng' : 'Ändra'}
                      </button>
                    </div>

                    {utfalld && (
                      <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
                        {valbara.map((kat) => (
                          <div key={kat.kategori}>
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              {kat.kategori}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                              {kat.grupper.map((g) => (
                                <label
                                  key={g.nyckel}
                                  className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={mina.includes(g.nyckel)}
                                    onChange={() => togglaYg(id, g.nyckel)}
                                    className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink"
                                  />
                                  <span className="min-w-0">
                                    {g.namn}
                                    {g.materialgrupper?.length > 0 && (
                                      <span className="text-gray-400"> + {g.materialgrupper.join(', ')}</span>
                                    )}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tillgangliga.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={spara}
            disabled={sparar || !osparadeAndringar || utanYrkesgrupp.length > 0}
            className="px-4 py-2 text-sm bg-leadme-pink text-white rounded hover:bg-leadme-pink-dark disabled:opacity-50"
          >
            {sparar ? 'Sparar…' : 'Spara underleverantörer'}
          </button>
          {utanYrkesgrupp.length > 0 ? (
            <span className="text-xs text-red-600">
              Kryssa i minst en yrkesgrupp för varje kopplad underleverantör
            </span>
          ) : osparadeAndringar && !sparar && (
            <span className="text-xs text-amber-600">Osparade ändringar</span>
          )}
        </div>
      )}
    </div>
  );
};

export default BuilderSubcontractorsTab;
